import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { Toggle, isDeletedFlag } from './fields';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';

/**
 * Bespoke edit drawer for Melrose Orders (cms.melrose_orders). Mostly read-only import
 * log (like EDI Orders) — the only editable field is Processed (Y/N). Fetches the full
 * record on open, shows a Melrose deep-link (melroseid), the raw JSON payload in a
 * collapsible viewer, and the error details; toggling Processed is the sole write.
 */
const TABLE = 'melrose-orders';
const s = (v: any): string => (v == null ? '' : String(v));
const yn = (v: any): 'Y' | 'N' => (s(v).toUpperCase() === 'Y' ? 'Y' : 'N');

const Field: React.FC<{ label: string; children?: React.ReactNode; wide?: boolean }> = ({ label, children, wide }) => (
  <div className={`flex flex-col${wide ? ' md:col-span-2' : ''}`}>
    <span className="text-xs font-medium text-muted mb-1">{label}</span>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg whitespace-pre-wrap break-words">
      {(children == null || children === '') ? <span className="text-gray-400">—</span> : children}
    </div>
  </div>
);

/**
 * Name + the underlying V1 recno (customers.recno / locations.recno) so LCs can tie the
 * Melrose feed's codes back to CMS. Renders '' (Field shows the em dash) when both are blank;
 * a 0 code is treated as no code.
 */
const nameWithCode = (name: any, code: any, deletedFlag?: any): React.ReactNode => {
  const n = titleCase(s(name));
  const c = Number(code) ? String(Number(code)) : '';
  if (!n && !c) return '';
  // The Melrose feed keeps pointing at whatever CMS record it matched, so an old order
  // can reference a since-deleted customer/location. Flag it the way the grid does.
  const gone = isDeletedFlag(deletedFlag) && !!(n || c);
  return (
    <span className={gone ? 'text-red-600 dark:text-red-400' : undefined}>
      {n}
      {c && <span className={`ml-2 text-xs ${gone ? 'text-red-600 dark:text-red-400' : 'text-muted'}`}>#{c}</span>}
      {gone && <span className="ml-1.5 text-[0.625rem] font-semibold">(deleted)</span>}
    </span>
  );
};

const MelroseOrdersEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const recno = row ? Number(row.recno) : 0;

  const [rec, setRec] = useState<TablemaintRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [processed, setProcessed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    if (!recno) return;
    setLoading(true);
    setLoadError(null);
    tablemaintService.get(TABLE, recno)
      .then((r) => { setRec(r); setProcessed(yn(r.processed) === 'Y'); })
      .catch((err) => setLoadError(err?.response?.data?.detail || err?.message || 'Failed to load Melrose order.'))
      .finally(() => setLoading(false));
  }, [recno]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (saveError) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [saveError]);

  // Prettify the raw JSON payload (fall back to the raw text if it isn't valid JSON).
  const prettyJson = useMemo(() => {
    const raw = s(rec?.jsondata);
    if (!raw) return '';
    try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
  }, [rec]);

  const dirty = rec != null && processed !== (yn(rec.processed) === 'Y');

  const handleSave = useCallback(async () => {
    if (!rec) return;
    if (!dirty) { onClose(); return; }
    setSaveError(null);
    setIsSaving(true);
    try {
      await tablemaintService.update(TABLE, recno, { processed: processed ? 'Y' : 'N' });
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save Melrose order.');
    } finally {
      setIsSaving(false);
    }
  }, [rec, dirty, processed, recno, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving || loading || !!loadError}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> Save Changes</>)}
      </button>
    </div>
  );

  const melroseId = s(rec?.melroseid);

  return (
    <Drawer isOpen onClose={onClose} title={`Melrose Order (#${recno})`} position="right" width="56rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        <div className="flex justify-end">
          <EditHistoryLink table={TABLE} recno={recno} />
        </div>
        {(loadError || saveError) && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
            {loadError || saveError}
          </div>
        )}

        {loading ? (
          <div className="py-12 text-center text-sm text-muted">Loading…</div>
        ) : rec ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Create Date">{s(rec.createdatetime)}</Field>
              <Field label="Created By">{s(rec.createdby)}</Field>
              <Field label="Melrose ID">
                {melroseId ? (
                  <a href={`https://app.getmelrose.com/dashboard/delivery_orders/${encodeURIComponent(melroseId)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline">
                    {melroseId}
                  </a>
                ) : null}
              </Field>
              <Field label="Move Type">{s(rec.movetype)}</Field>
              <Field label="Customer">{nameWithCode(rec.custname, rec.customercode, rec.custdeleted)}</Field>
              <Field label="Consignee">{nameWithCode(rec.consignee, rec.consigneecode, rec.consigneedeleted)}</Field>
              <Field label="BOL">{s(rec.mbl)}</Field>
              <Field label="Booking #">{s(rec.bookingnumber)}</Field>
              <Field label="Bill To Ref #">{s(rec.billtorefno)}</Field>
              <Field label="Process Date/Time">{s(rec.processdatetime)}</Field>
            </div>

            {/* Errors — read-only textarea, full width. */}
            <div>
              <label className="block text-sm font-medium text-label mb-1">Errors</label>
              <textarea readOnly value={s(rec.errordetails)} rows={4}
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/60 text-gray-800 dark:text-gray-200 focus:outline-none" />
            </div>

            {/* Processed — the only editable field. */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <Toggle label="Processed" checked={processed} disabled={isSaving} onChange={setProcessed}
                hint="Marks this Melrose order as processed. The only editable field on this record." />
            </div>

            {/* Raw JSON payload — collapsible. */}
            {prettyJson && (
              <details className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <summary className="cursor-pointer select-none text-sm font-medium text-label">Raw Results</summary>
                <pre className="mt-2 max-h-96 overflow-auto rounded-md bg-gray-50 dark:bg-gray-900/60 p-3 text-xs text-gray-800 dark:text-gray-200">
                  {prettyJson}
                </pre>
              </details>
            )}
          </>
        ) : null}
      </div>
    </Drawer>
  );
};

export default MelroseOrdersEditDrawer;
